import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { usePaginatedData } from '~/lib/hooks/usePaginatedData';
import { classNames as cn } from '~/utils/classNames';
import { Pagination } from '~/components/ui/Pagination';
import { SortIndicator, type SortDir } from '~/components/ui/SortIndicator';

interface ResultsTableProps {
  data: any[];
  onRowClick?: (row: any) => void;
  isDefaultQuery: boolean;
  defaultLimit?: number;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
  onSortChange?: (sortField: string, sortDirection: SortDir) => void;
  noResultLabel?: string;
}

const DEFAULT_PAGE_SIZE = 25;

export function ResultsTable({
  data,
  onRowClick,
  isDefaultQuery,
  defaultLimit,
  sortField,
  sortDirection,
  onSortChange,
  noResultLabel = 'No result found.',
}: ResultsTableProps) {
  const {
    data: paginatedData,
    page,
    onPageChange,
    totalPages,
  } = usePaginatedData(data, {
    pageSize: DEFAULT_PAGE_SIZE,
  });

  if (!data || data.length === 0) {
    return <p className="text-left text-bolt-ds-textSecondary">{noResultLabel}</p>;
  }

  const headers = Object.keys(data[0]);

  return (
    <div className="overflow-auto">
      <div className="w-full flex flex-col gap-4">
        {data && (
          <Pagination
            page={page}
            pageSize={DEFAULT_PAGE_SIZE}
            totalPages={totalPages}
            totalItems={data.length}
            allowPageInput
            onPageChange={onPageChange}
            descriptionNode={isDefaultQuery ? `Showing up to ${defaultLimit} records` : undefined}
            className="rounded-b-[0px] mb-[-16px] border-b-transparent"
          />
        )}
        <Table className="border border-bolt-ds-borderOutline rounded-md">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {headers.map((header) => (
                <TableHead className="first:pl-6 lg:first:pl-8 last:pr-6 lg:last:pr-8" key={header}>
                  <div className="flex items-center gap-1">
                    {header}
                    {header && (
                      <SortIndicator
                        onSortChange={onSortChange}
                        activeField={header}
                        sortField={sortField}
                        sortDirection={sortDirection}
                      />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={() => onRowClick?.(row)}
                className={cn(onRowClick && 'cursor-pointer hover:bg-bolt-ds-surfaceThree/50 group')}
              >
                {headers.map((header) => (
                  <TableCell
                    className="first:pl-6 lg:first:pl-8 last:pr-6 lg:last:pr-8 text-xs text-bolt-ds-textPrimary group-hover:text-bolt-ds-textPrimary min-w-[8rem]"
                    key={`${rowIndex}-${header}`}
                  >
                    <div className="text-xs font-mono w-fit max-w-96 truncate">
                      {JSON.stringify(row[header]).replace(/^"|"$/g, '')}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
